import React, { useMemo, useState } from 'react';
import { upcomingEvents } from '../data/upcoming';
import { siteMeta } from '../data/siteMeta';

const TYPE_STYLE = {
  effective:   { label: 'Takes effect',   cls: 'bg-green-100 text-green-700 border-green-200',   icon: 'fa-calendar-check' },
  deadline:    { label: 'Deadline',       cls: 'bg-red-100 text-red-700 border-red-200',         icon: 'fa-hourglass-half' },
  court:       { label: 'Ruling pending', cls: 'bg-amber-100 text-amber-700 border-amber-200',   icon: 'fa-gavel' },
  enforcement: { label: 'Enforcement',    cls: 'bg-purple-100 text-purple-700 border-purple-200', icon: 'fa-shield-halved' },
  legislative: { label: 'Vote expected',  cls: 'bg-blue-100 text-blue-700 border-blue-200',      icon: 'fa-landmark' },
};

function horizonOf(iso) {
  if (iso < '2027-01-01') return 'Q4 2026';
  if (iso < '2027-07-01') return 'H1 2027';
  if (iso < '2028-01-01') return 'H2 2027';
  return '2028+';
}

const HORIZONS = ['Q4 2026', 'H1 2027', 'H2 2027', '2028+'];

function linkFor(ev) {
  const p = new URLSearchParams();
  if (ev.tab && ev.tab !== 'all') p.set('tab', ev.tab);
  if (ev.q) p.set('q', ev.q);
  const qs = p.toString();
  return qs ? `?${qs}` : '/';
}

function UpcomingWatchlist() {
  const [open, setOpen] = useState(true);
  const [filter, setFilter] = useState('all');

  const grouped = useMemo(() => {
    const sorted = [...upcomingEvents]
      .filter(e => filter === 'all' || e.type === filter)
      .sort((a, b) => a.date.localeCompare(b.date));
    const g = {};
    sorted.forEach(e => { const h = horizonOf(e.date); (g[h] = g[h] || []).push(e); });
    return g;
  }, [filter]);

  const counts = useMemo(() => {
    const c = { all: upcomingEvents.length };
    upcomingEvents.forEach(e => { c[e.type] = (c[e.type] || 0) + 1; });
    return c;
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6" aria-labelledby="watchlist-heading">
      <div className="bg-slate-50 rounded-lg border border-slate-200">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
          aria-expanded={open}
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-purple-100 text-purple-700">
              <i className="fas fa-binoculars"></i>
            </span>
            <div>
              <h2 id="watchlist-heading" className="text-lg font-bold text-gray-900">What's Next: 2026–2028 Watchlist</h2>
              <p className="text-xs text-gray-500">
                {upcomingEvents.length} upcoming effective dates, deadlines and pending rulings · {siteMeta.updateLabel} · next review {siteMeta.nextReview}
              </p>
            </div>
          </div>
          <i className={`fas fa-chevron-${open ? 'up' : 'down'} text-gray-400`}></i>
        </button>

        {open && (
          <div className="px-5 pb-5">
            <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Filter watchlist by type">
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${filter === 'all' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'}`}
              >
                All ({counts.all})
              </button>
              {Object.entries(TYPE_STYLE).map(([key, s]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${filter === key ? s.cls + ' ring-2 ring-offset-1 ring-gray-300' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                >
                  <i className={`fas ${s.icon} mr-1`}></i>{s.label} ({counts[key] || 0})
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {HORIZONS.filter(h => grouped[h]?.length).map(h => (
                <div key={h}>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{h}</h3>
                  <ul className="space-y-2">
                    {grouped[h].map((ev, i) => {
                      const s = TYPE_STYLE[ev.type];
                      return (
                        <li key={`${h}-${i}`} className="bg-white rounded-lg border border-slate-200 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${s.cls}`}>
                                  <i className={`fas ${s.icon}`}></i> {s.label}
                                </span>
                                <span className="text-xs font-semibold text-gray-700">{ev.dateLabel}</span>
                                <span className="text-xs text-gray-400">· {ev.jurisdiction}</span>
                              </div>
                              <a href={linkFor(ev)} className="text-sm font-semibold text-gray-900 hover:text-purple-700 leading-snug">
                                {ev.title}
                              </a>
                              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{ev.detail}</p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-4">
              Dates reflect announced or statutory schedules as of {siteMeta.updatedOn}. Court timelines are estimates. Click any item to jump to the related law cards.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export default UpcomingWatchlist;
